import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Search, ShieldCheck, ArrowRight } from "lucide-react";
import { api, track } from "@/lib/api";
import { MerchantNote } from "./MerchantNote";

/**
 * Central commercial module. Never hard-codes a merchant; offers resolve server-side.
 * When no approved offer exists, the CTA renders in a disabled state.
 */
export const CommercialModule = ({
  category,
  fallback,
  label,
  headline,
  note,
  placement = "CONTEXTUAL CTA",
  articleSlug,
  toolSlug,
  compact = false,
  size = "md",
  tone = "default",
  anchorTo = null,
  allowLive = false,
  showDisclosure = true,
  merchantLabel = false,
  testKey = null,
}) => {
  const [res, setRes] = useState(null);
  const seen = useRef(false);

  useEffect(() => {
    if (!category || anchorTo) return;
    api
      .get("/offers/resolve", { params: { category, fallback } })
      .then((r) => setRes(r.data))
      .catch(() => {});
  }, [category, fallback]);

  useEffect(() => {
    if (!res || seen.current) return;
    seen.current = true;
    track({
      event_type: "cta_impression",
      cta_placement: placement,
      offer_category: category,
      article_slug: articleSlug,
      tool_slug: toolSlug,
      merchant: res.offer?.merchant,
    });
  }, [res, placement, category, articleSlug, toolSlug]);

  if (!category) return null;
  // Live merchant links only render where a placement has been explicitly approved. Everywhere
  // else the module stays in its disabled preview state even if the category has an active offer.
  const live = allowLive && !anchorTo && res?.status === "active";
  // An in-site options destination is used when this placement is not an approved affiliate spot,
  // so the visitor still reaches real options instead of an inert button.
  const destination = !anchorTo && !live ? res?.options_destination || null : null;
  const researching = !anchorTo && !live && !destination && res !== null;

  const onClick = () => {
    track({
      event_type: "cta_click",
      cta_placement: placement,
      offer_category: category,
      article_slug: articleSlug,
      tool_slug: toolSlug,
      offer_id: res?.offer?.id,
      merchant: res?.offer?.merchant,
    });
  };

  const key = testKey || category;

  const sizing =
    size === "lg" ? "px-9 py-[17px] text-[13px] tracking-[0.16em]" : "px-7 py-3.5 text-[12.5px] tracking-[0.14em]";

  // Opt-in: a live placement can take its wording from the offer record, so the merchant is named
  // in the button itself without page copy hard-coding a merchant.
  const ctaText = live && merchantLabel ? res.offer?.cta_label || label : label;

  const disabledStyle =
    tone === "strong"
      ? "border border-[#C6C1B7] bg-[#E3DFD7] text-[#3F3F3F] font-semibold"
      : "border border-line bg-[#EDEAE4] text-[#3F3F3F] font-semibold";

  const button = anchorTo ? (
    <a
      href={anchorTo}
      onClick={(e) => {
        e.preventDefault();
        document
          .querySelector(anchorTo)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
      data-testid={`cta-anchor-${key}`}
      className={`inline-flex items-center gap-2 ${sizing} bg-sage text-white uppercase font-semibold hover:bg-sage-dark transition-colors duration-300`}
    >
      {label} <ArrowRight size={15} />
    </a>
  ) : live ? (
    <a
      href={res.offer.tracking_url}
      rel="sponsored nofollow noopener"
      target="_blank"
      onClick={onClick}
      data-testid={`cta-active-${key}`}
      className={`inline-flex items-center gap-2 ${sizing} bg-sage text-white uppercase hover:bg-sage-dark transition-colors duration-300`}
    >
      {ctaText} <ArrowRight size={15} />
    </a>
  ) : destination ? (
    <Link
      to={destination}
      onClick={onClick}
      data-testid={`cta-options-${key}`}
      className={`inline-flex items-center gap-2 ${sizing} bg-sage text-white uppercase font-semibold hover:bg-sage-dark transition-colors duration-300`}
    >
      {label} <ArrowRight size={15} />
    </Link>
  ) : researching ? (
    <p
      data-testid={`cta-researching-${key}`}
      className={
        compact
          ? "text-[13.5px] leading-relaxed text-[#6E6A63]"
          : "text-[14.5px] leading-relaxed text-[#4F4B45] max-w-md"
      }
    >
      <Search size={14} className="inline mr-1.5 text-sage-dark" />
      {compact
        ? "Options being researched"
        : "We are still researching options in this category. Nothing is listed here until a partner meets our standards, so use the guidance and planning tools on this page for now."}
    </p>
  ) : (
    <button
      type="button"
      disabled
      data-testid={`cta-disabled-${key}`}
      className={`inline-flex items-center gap-2 ${sizing} ${disabledStyle} uppercase cursor-not-allowed`}
    >
      <Lock size={14} /> {label}
    </button>
  );

  const disclosure = live && showDisclosure ? (
    <p
      className={
        tone === "strong"
          ? "text-[14px] text-[#454545] leading-relaxed max-w-sm"
          : "text-[11.5px] font-light text-[#6E6A63] leading-relaxed max-w-sm"
      }
    >
      <ShieldCheck size={12} className="inline mr-1 text-sage-dark" />
      Affiliate link. Human Home Living may earn a commission at no cost to you.
    </p>
  ) : null;

  // Identification is separate from the disclosure: a live merchant link always names its merchant.
  const merchantNote = live ? (
    <MerchantNote merchant={res.offer?.merchant} testId={`cta-merchant-${key}`} />
  ) : null;

  if (compact)
    return (
      <div data-testid={`commercial-compact-${key}`} data-cta-placement={placement}>
        {button}
        {merchantNote}
        {disclosure && <div className="mt-3">{disclosure}</div>}
      </div>
    );

  return (
    <aside
      data-testid={`commercial-module-${key}`}
      data-cta-placement={placement}
      className="my-12 border border-line bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 px-6 md:px-8 pt-5 pb-3 border-b border-line/80">
        <span className="text-[11px] tracking-[0.16em] uppercase text-[#4F4B45] font-medium">Options</span>
        {live && (
          <span
            data-testid={`cta-merchant-label-${key}`}
            className="text-[11px] tracking-[0.16em] uppercase text-[#48563F] font-semibold"
          >
            Affiliate link · {res.offer?.merchant}
          </span>
        )}
      </div>

      <div className="px-6 md:px-8 py-7">
        <h4
          data-testid={`cta-headline-${key}`}
          className="display text-[24px] md:text-[27px] leading-snug text-charcoal"
        >
          {headline}
        </h4>
        {note && (
          <p className="text-[15px] text-[#4A4A4A] mt-3 leading-relaxed max-w-xl">{note}</p>
        )}

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
          {button}
          {disclosure}
        </div>
        {merchantNote}
      </div>
    </aside>
  );
};

export const BarrierBadge = ({ barrier, testId = "barrier-badge" }) =>
  barrier ? (
    <span
      data-testid={testId}
      className="inline-flex items-center rounded-full px-3 py-1 text-[11.5px] tracking-[0.12em] uppercase font-medium bg-sage-soft text-[#48563F] border border-sage/35"
    >
      {barrier}
    </span>
  ) : null;

export const RoleBadge = ({ role }) =>
  role ? (
    <span className="inline-flex items-center rounded-full px-3 py-1 text-[10.5px] tracking-[0.14em] uppercase border border-line text-[#7A766F]">
      {role}
    </span>
  ) : null;
